//
// # Symbol Graphics Catalogue
//
// Pre-defined standard graphics shapes for common symbol types.
// Each entry produces an array of graphics.Element objects
// translated to the given center position.
//

import { graphics, randomId } from "GenAlphaModel";

export interface CatalogueEntry {
  readonly name: string;
  readonly create: (cx: number, cy: number) => Array<graphics.Element>;
}

// Stamp all elements in an array with a shared groupId.
// Single-element shapes are left ungrouped.
function grouped(elements: Array<graphics.Element>): Array<graphics.Element> {
  if (elements.length <= 1) return elements;
  const groupId = randomId();
  return elements.map((e) => ({ ...e, groupId }));
}

// Helper: translate a polygon to a center position
function poly(
  cx: number,
  cy: number,
  pts: Array<[number, number]>,
  filled = false
): graphics.Polygon {
  return graphics.Polygon.new({
    points: pts.map(([x, y]) => ({ x: x + cx, y: y + cy })),
    filled,
  });
}

// Helper: translate a path to a center position
function path(
  cx: number,
  cy: number,
  pts: Array<[number, number]>
): graphics.Path {
  return graphics.Path.new({
    points: pts.map(([x, y]) => ({ x: x + cx, y: y + cy })),
    style: graphics.PathStyle.default(),
  });
}

// Helper: create a small circle (bubble) at a position
function bubble(cx: number, cy: number, r: number): graphics.Ellipse {
  return graphics.Ellipse.new({
    bbox: {
      min: { x: cx - r, y: cy - r },
      max: { x: cx + r, y: cy + r },
    },
    filled: false,
  });
}

// Helper: create a text label
function label(
  cx: number,
  cy: number,
  text: string,
  height = 14
): graphics.Text {
  return graphics.Text.new({
    text,
    loc: { x: cx, y: cy },
    height,
    hidden: false,
    alignment: "center",
    baseline: "middle",
    rotation: 0,
  });
}

//
// Shape definitions (centered at origin, translated to cx, cy)
//

function opAmp(cx: number, cy: number): Array<graphics.Element> {
  return grouped([
    poly(cx, cy, [
      [-60, -50],
      [-60, 50],
      [60, 0],
    ]),
    label(cx - 40, cy - 22, "+"),
    label(cx - 40, cy + 22, "-"),
  ]);
}

function inverter(cx: number, cy: number): Array<graphics.Element> {
  return grouped([
    poly(cx, cy, [
      [-50, -40],
      [-50, 40],
      [40, 0],
    ]),
    bubble(cx + 48, cy, 8),
  ]);
}

function diode(cx: number, cy: number): Array<graphics.Element> {
  return grouped([
    poly(cx, cy, [
      [-20, -30],
      [-20, 30],
      [20, 0],
    ]),
    path(cx, cy, [
      [20, -30],
      [20, 30],
    ]),
  ]);
}

function voltageSource(cx: number, cy: number): Array<graphics.Element> {
  return grouped([
    bubble(cx, cy, 30),
    label(cx, cy - 12, "+"),
    label(cx, cy + 12, "-"),
  ]);
}

//
// Catalogue
//
export const CATALOGUE: ReadonlyArray<CatalogueEntry> = [
  { name: "Op-Amp", create: opAmp },
  { name: "Inverter", create: inverter },
  { name: "Diode", create: diode },
  { name: "Voltage Source", create: voltageSource },
];
